import { ErrorBoundary } from '@sentry/react';
import { AnalyticsService } from 'base/analytics';
import { delay, exists, keyOfObject } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { getShadeKey } from 'base/styleguide/concept';
import { Button } from 'base/ui/button';
import { BLOCK_DRAG_START } from 'base/ui/dnd';
import { formatError } from 'base/ui/form';
import { IconAdd, IconAddComponent, IconLock, IconStars } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { globalResizeObserver } from 'base/ui/measure';
import { menuItem, subMenu } from 'base/ui/menu_model';
import { ClickInTextarea } from 'base/ui/textarea';
import { TextRegular, TextTinyLight } from 'base/ui/typography';
import {
  makeDropdownDecorator,
  makeFormVisibilityDecorator,
  makeNavbarDecorator,
  makeSliderDecorator,
  makeTabsDecorator,
} from 'blocks/component_renderer/decorators';
import { ElementView } from 'blocks/component_renderer/element_view';
import { getBreakpointForViewport } from 'blocks/component_renderer/styles';
import { SectionTextEditingModel } from 'blocks/component_renderer/text_overrides';
import { allSlugsInElementTree } from 'blocks/editor/model_helpers';
import { UndoManager } from 'blocks/woot_woot/undo';
import classNames from 'classnames';
import { comparer, computed, reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { expr } from 'mobx-utils';
import React from 'react';
import { syncSlotOverrides } from '../../component_renderer/slots_syncing';
import { NotificationsContainer, TaskNotifications } from '../ai/notifications';
import { isUserWrittenName } from '../ai/page_sections';
import { ProjectEditingActions } from '../editing_actions/project';
import { SectionEditingActions } from '../editing_actions/section';
import { EditorMode } from '../editor_mode';
import {
  ElementInstance,
  ElementInstanceState,
  MISSING_GLOBAL_SECTION_STATE,
  Page,
  PageState,
  Section,
  SectionRef,
  SectionRefState,
  SectionState,
} from '../model';
import { ADD_BELOW_ACTION_POSITION, DragHandleOpts } from '../selection/frame';
import {
  useClickOrHoverToSelect,
  useSelectable,
} from '../selection/selectable';
import { SelectionStore } from '../selection/store';
import { SelectionActions } from '../selection_actions';
import { ProjectSession } from '../session/session';
import { SidebarNavigation } from '../sidebar/routes';
import { useCommonSectionInteractions } from '../sitemap/section_common';
import {
  makeDropBeforeTarget,
  makeSectionSplitDropTarget,
  sectionDndDomain,
} from '../sitemap/section_dnd';
import { ConceptAPI } from '../styleguide/concept_store';
import {
  StyledElementsContext,
  StyledElementView,
} from '../styleguide/styled_element_view';
import { globalSuggestionsStore } from '../suggested_edits';
import {
  Breakpoint,
  breakpointStore,
  BREAKPOINT_SIZE,
  BREAKPOINT_TO_CANVAS_SCALE,
} from './breakpoint';
import {
  DEFAULT_ELEMENT_VIEW_OPTIONS,
  globalElementViewOptions,
} from './element_view_options';
import styles from './section.css';

/**
 * Ensures that the slotOverrides property is set on the ElementInstance,
 * for each slot that is tagged in the element's webflow json.
 *
 * (Users can not edit unless the slotOverride instances are present.)
 **/
function ensureSlotOverridesEffect(
  rootElement: ElementInstance,
  library: LibraryCache,
  undo: Pick<UndoManager, 'doPermanently'>,
) {
  return reaction(
    () => {
      const slugs = allSlugsInElementTree(rootElement);
      return slugs.map(
        (slug) => library.getCachedPublicComponentJson(slug)?.value,
      );
    },
    () => {
      syncSlotOverrides({
        element: rootElement,
        library,
        undo,
      });
    },
    { fireImmediately: true },
  );
}

export const PageSectionsView = observer(function _PageSectionsView({
  page,
  session,
  selection,
  selectionActions,
  analytics,
  editing,
  library,
  breakpoint,
  outerWidth,
  concept,
}: {
  page: Page | PageState;
  session: ProjectSession;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  editing: ProjectEditingActions | undefined;
  library: LibraryCache;
  breakpoint?: Breakpoint;
  outerWidth?: number;
  concept?: ConceptAPI;
}) {
  const renderDragPreview = React.useMemo(() => {
    return concept != null
      ? ({ children }: { children: React.ReactNode }) => (
          <StyledElementsContext concept={concept} library={library}>
            {children}
          </StyledElementsContext>
        )
      : undefined;
  }, [concept, library]);

  const dndState = sectionDndDomain.state.get();
  const mutablePage = page.mutable ? page : undefined;
  const commonSectionViewProps = {
    selection,
    selectionActions,
    session,
    library,
    analytics,
    breakpoint,
    outerWidth,
    concept,
  };
  const pageOnlySelected = expr(() => {
    const sel = selection.get();
    return sel.page === page && sel.section == null;
  });

  if (page.mutable && !session.canViewWireframe(page)) {
    return (
      <UpgradeToViewWireframe
        page={page}
        selection={selection}
        sidebarNavigation={editing?.sidebarNavigation}
        session={session}
        editing={editing}
      />
    );
  }

  function renderActualSection(
    sref: SectionRef | SectionRefState,
    indexInPage: number,
  ) {
    return (
      <sectionDndDomain.Draggable
        key={keyOfObject(sref)}
        source={
          mutablePage && sref.mutable && editing
            ? {
                section: sref.state,
                from: {
                  page: mutablePage,
                  index: indexInPage,
                  mutable: sref,
                },
              }
            : undefined
        }
        supportsDuplicate={true}
        selfDropTarget={
          mutablePage && { page: mutablePage, index: indexInPage }
        }
        onAfterDrop={editing?.onAfterDropSection}
        dropTarget={makeSectionSplitDropTarget(page, indexInPage, editing)}
        renderDragPreview={renderDragPreview}
      >
        <SectionView
          sref={sref}
          editing={editing}
          {...commonSectionViewProps}
        />
      </sectionDndDomain.Draggable>
    );
  }

  function renderDropPreview(indexInPage: number) {
    if (dndState?.dest?.page !== page || dndState?.dest.index !== indexInPage)
      return;
    return (
      <sectionDndDomain.DropTarget
        key={`d-${indexInPage}`}
        opts={makeDropBeforeTarget(page, indexInPage, editing)}
        className={styles.dropPreview}
      >
        <div className={styles.dropPreviewInner}>
          <SectionView
            sref={dndState.source.section}
            isDropHovering={true}
            editing={undefined}
            {...commonSectionViewProps}
          />
        </div>
      </sectionDndDomain.DropTarget>
    );
  }

  function renderSuggestions(indexInPage: number) {
    if (!page.mutable) return [];
    const suggestions = globalSuggestionsStore.getSuggestedSections(
      page,
      indexInPage,
    );
    return suggestions.map((suggestion, i) => (
      <sectionDndDomain.DropTarget
        key={`s-${indexInPage}-${i}`}
        opts={makeDropBeforeTarget(page, indexInPage, editing)}
      >
        <SectionView
          sref={suggestion}
          isSuggestion={true}
          editing={undefined}
          {...commonSectionViewProps}
        />
      </sectionDndDomain.DropTarget>
    ));
  }

  function renderPending(indexInPage: number) {
    if (indexInPage < page.sections.length) return;
    if (!page.mutable || !selection.decorations.arePageSectionsPending(page))
      return;
    return [0, 1, 2].map((i) => (
      <sectionDndDomain.DropTarget
        key={`p-${i}`}
        opts={makeDropBeforeTarget(page, indexInPage, editing)}
      >
        <PendingSection index={i} />
      </sectionDndDomain.DropTarget>
    ));
  }

  const isEmpty =
    page.sections.length === 0 &&
    !(page.mutable && globalSuggestionsStore.hasSuggestedSections(page));
  return (
    <sectionDndDomain.DropTarget
      className={classNames(styles.pageSections, isEmpty && styles.isEmpty)}
      opts={makeDropBeforeTarget(page, 0, editing)}
    >
      {page.sections.flatMap((section, indexInPage) => [
        renderDropPreview(indexInPage),
        ...renderSuggestions(indexInPage),
        renderActualSection(section, indexInPage),
      ])}
      {renderDropPreview(page.sections.length)}
      {renderSuggestions(page.sections.length)}
      {renderPending(page.sections.length)}
      {isEmpty && editing && page.mutable && (
        <div
          className={classNames(
            styles.emptyButtons,
            pageOnlySelected && styles.forceShow,
          )}
        >
          <Button
            icon={IconAdd}
            label="Section"
            onClick={() => {
              selection.set(selection.factory.forPage(page), {
                disableEnsureVisible: true,
              });
              editing.sidebarNavigation.toggleToplevel('add');
            }}
          />
          <Button
            icon={IconStars}
            label="Generate page"
            variant="secondary"
            iconColor="ai"
            onClick={editing.forPage(page).regenerateOrOpenForm}
          />
        </div>
      )}
    </sectionDndDomain.DropTarget>
  );
});

const UpgradeToViewWireframe = observer(function _UpgradeToViewWireframe({
  session,
  selection,
  sidebarNavigation,
  page,
  editing,
}: {
  session: ProjectSession;
  selection: SelectionStore;
  sidebarNavigation: SidebarNavigation | undefined;
  page: Page;
  editing: ProjectEditingActions | undefined;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  const selTarget = selection.factory.forPage(page);
  useClickOrHoverToSelect(ref, selection, selTarget, {
    onSelect: () => {
      sidebarNavigation?.goto({ path: 'edit' });
    },
  });

  return (
    <div
      ref={ref}
      className={styles.upgrade}
      style={{ ['--numSections' as string]: page.sections.length }}
    >
      <div className={styles.upgradeButton}>
        <Button
          label="Upgrade to view wireframe"
          icon={IconLock}
          disabled={!session.locked && editing == null}
          onClick={() => session.showUpgradeWall?.('pages')}
        />
      </div>
    </div>
  );
});

export function PageSectionsTail({
  page,
  editing,
}: {
  page: Page | PageState;
  editing: ProjectEditingActions | undefined;
}) {
  const enableDnd = !page.mutable || editing?.session.canViewWireframe(page);
  return (
    <sectionDndDomain.DropTarget
      className={styles.pageSectionsTail}
      {...BLOCK_DRAG_START}
      opts={
        enableDnd
          ? makeDropBeforeTarget(page, page.sections.length, editing)
          : undefined
      }
    />
  );
}

function PendingSection({ index }: { index: number }) {
  return (
    <div className={styles.pendingSection}>
      <div
        className={styles.pendingSectionPulsar}
        style={{ ['--index' as string]: index }}
      />
    </div>
  );
}

const dimensionsCache = new WeakMap<
  Section | SectionState,
  { width: number; height: number }
>();

const SectionView = observer(function _SectionView({
  sref,
  isSuggestion: sectionIsSuggestion = false,
  isDropHovering = false,
  selection,
  selectionActions,
  analytics,
  editing,
  session,
  library,
  breakpoint,
  outerWidth,
  concept,
}: {
  sref: SectionRef | SectionRefState;
  isSuggestion?: boolean;
  isDropHovering?: boolean;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  editing: ProjectEditingActions | undefined;
  session: ProjectSession;
  library: LibraryCache;
  breakpoint?: Breakpoint;
  outerWidth?: number;
  concept?: ConceptAPI;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  const section: Section | SectionState =
    sref.type === 'inline'
      ? sref.value
      : selection.project.globalSections.get(sref.id) ||
        MISSING_GLOBAL_SECTION_STATE;

  const isAdding = editing?.sidebarNavigation.toplevel === 'add';

  const sectionEditing = sref.mutable ? editing?.forSection(sref) : undefined;
  const selectionFrameAction =
    editing && sref.mutable && !isAdding
      ? {
          showWhenSelected: false,
          showWhenHovering: true,
          pos: ADD_BELOW_ACTION_POSITION,
          maxWidthRatio: 0.75,
          content: (
            <Button
              variant="primary"
              background={sref.type === 'reference' ? 'global' : 'ai'}
              icon={IconAdd}
              label="Section"
              onClick={() => {
                selection.set(selection.factory.forSection(sref), {
                  disableEnsureVisible: true,
                });
                editing.sidebarNavigation.toggleToplevel('add');
              }}
            />
          ),
        }
      : undefined;
  const [isSelected, selectableClassName] = sref.mutable
    ? // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useSelectable(ref, selection, selection.factory.forSection(sref), {
        frameColor: sref.type === 'reference' ? 'global' : undefined,
        actions: { add: selectionFrameAction },
        dragHandle: sectionEditing ? DRAG_HANDLE_OPTS : undefined,
        onSelect: editing?.sidebarNavigation.openEditPanelIfClosed,
      })
    : [false, ''];
  const { element, isSuggestion: elementIsSuggestion } = section.mutable
    ? globalSuggestionsStore.getElement(section)
    : { element: section.element.current, isSuggestion: false };
  const isSuggestion = sectionIsSuggestion || elementIsSuggestion;

  React.useLayoutEffect(
    () => {
      /**
       * Use case 1:
       *   Our DnD implementation assumes that things don't change size.
       *   ComponentView is a mess of self measuring bullshit.
       *   So that it doesn't mount at 0 size and then expand, we cache the
       *   dimensions from previous mounts and use that for the first tick.
       *   Oh god.
       * Use case 2:
       *   Avoid flicker of 0 height when switching components, or
       *   previewing a component.
       */
      const el = ref.current;
      if (!el) return;
      const cached = dimensionsCache.get(section);
      if (cached) {
        el.style.width = `${cached.width}px`;
        el.style.height = `${cached.height}px`;
        delay(50).then(() => {
          el.style.width = '';
          el.style.height = '';
        });
      } else {
        const dims = { width: el.offsetWidth, height: el.offsetHeight };
        dimensionsCache.set(section, dims);
      }
      return globalResizeObserver.observe(el, () => {
        const dims = { width: el.offsetWidth, height: el.offsetHeight };
        dimensionsCache.set(section, dims);
      });
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [element],
  );

  const [onContextMenu, menuPortal] = useCommonSectionInteractions({
    sectionEditing,
    projectEditing: editing,
    selectionActions,
    analytics,
    isSelected,
    extraMenuItems: sectionEditing
      ? [
          subMenu({
            label: 'Reset component',
            menu: [
              menuItem({
                label: 'Reset all changes',
                onClick: () => {
                  sectionEditing.resetElements?.();
                  sectionEditing.resetCopy?.();
                  analytics.track({
                    event: 'blocks/reset',
                    object: 'all',
                    subject: 'section',
                    componentSlugs: element ? [element.source.slug] : [],
                  });
                },
                disabled:
                  sectionEditing.resetElements == null &&
                  sectionEditing.resetCopy == null,
                disabledReason: 'You haven’t changed the elements or copy',
              }),
              menuItem({
                label: 'Reset elements',
                onClick: () => {
                  sectionEditing.resetElements!();
                  analytics.track({
                    event: 'blocks/reset',
                    object: 'elements',
                    subject: 'section',
                    componentSlugs: element ? [element.source.slug] : [],
                  });
                },
                disabled: sectionEditing.resetElements == null,
                disabledReason: 'You haven’t changed the elements',
              }),
              menuItem({
                label: 'Reset copy',
                onClick: () => {
                  sectionEditing.resetCopy!();
                  analytics.track({
                    event: 'blocks/reset',
                    object: 'copy',
                    subject: 'section',
                    componentSlugs: element ? [element.source.slug] : [],
                  });
                },
                disabled: sectionEditing.resetCopy == null,
                disabledReason: 'You haven’t changed the copy',
              }),
            ],
          }),
        ].filter(exists)
      : [],
  });

  return (
    <div
      ref={ref}
      className={classNames(
        styles.section,
        isSuggestion && styles.isSuggestion,
        isDropHovering && styles.isDropHovering,
        selectableClassName,
      )}
      onContextMenu={onContextMenu}
    >
      {element ? (
        <ErrorBoundary
          key={keyOfObject(element)}
          fallback={<ErrorSectionContent error="Error displaying component" />}
        >
          {concept != null ? (
            <StyledSectionContent
              sref={sref}
              element={element}
              selectionActions={selectionActions}
              isSelected={isSelected}
              editing={sectionEditing}
              session={session}
              selection={selection}
              library={library}
              analytics={analytics}
              breakpoint={breakpoint}
              outerWidth={outerWidth}
              concept={concept}
            />
          ) : (
            <SectionContent
              sref={sref}
              element={element}
              selectionActions={selectionActions}
              isSelected={isSelected}
              editing={sectionEditing}
              session={session}
              selection={selection}
              library={library}
              analytics={analytics}
              breakpoint={breakpoint}
              outerWidth={outerWidth}
            />
          )}
        </ErrorBoundary>
      ) : (
        <EmptySectionContent
          sref={sref}
          section={section}
          editing={sectionEditing}
          projectEditing={editing}
          isSelected={isSelected}
        />
      )}
      <NotificationsContainer offset="wireframe">
        {editing && section.mutable && (
          <TaskNotifications
            editing={editing}
            target={section}
            mode={EditorMode.Wireframe}
          />
        )}
      </NotificationsContainer>
      {menuPortal}
    </div>
  );
});

const DRAG_HANDLE_OPTS: DragHandleOpts = {
  domain: sectionDndDomain,
  draggablePosition: 'parent',
  handlePosition: 'left',
};

export function makeSectionVisibilityDecorators(
  element: ElementInstance | ElementInstanceState,
) {
  const viewOptions = element.mutable
    ? globalElementViewOptions.get(element)
    : DEFAULT_ELEMENT_VIEW_OPTIONS;
  return [
    makeDropdownDecorator(viewOptions),
    makeNavbarDecorator(viewOptions),
    makeFormVisibilityDecorator(),
    makeSliderDecorator({ sliderIndex: 0 }),
    makeTabsDecorator(viewOptions),
  ];
}

const SectionContent = observer(function _SectionContent({
  sref,
  element,
  selectionActions,
  isSelected,
  editing,
  session,
  selection,
  library,
  analytics,
  breakpoint,
  outerWidth,
}: {
  sref: SectionRef | SectionRefState;
  element: ElementInstance | ElementInstanceState;
  selectionActions: SelectionActions;
  isSelected: boolean;
  editing: SectionEditingActions | undefined;
  session: ProjectSession;
  selection: SelectionStore;
  library: LibraryCache;
  analytics: AnalyticsService;
  breakpoint?: Breakpoint;
  outerWidth?: number;
}) {
  const textEditing = React.useMemo(
    () =>
      sref.mutable && element.mutable && editing
        ? new SectionTextEditingModel(
            selection,
            sref,
            analytics,
            editing.textRewriting,
          )
        : undefined,
    [selection, element, sref, analytics, editing],
  );

  const visibilityDecorators = React.useMemo(
    () => makeSectionVisibilityDecorators(element),
    [element],
  );

  React.useEffect(
    () =>
      element.mutable && editing
        ? ensureSlotOverridesEffect(element, library, session.undo)
        : undefined,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [element, session, editing],
  );

  const bp = BREAKPOINT_SIZE[breakpoint ?? breakpointStore.get()];
  const breakpoints = React.useMemo(
    () => getBreakpointForViewport(bp.width),
    [bp],
  );

  return (
    <ElementView
      sref={sref}
      rootElement={element}
      textEditing={textEditing}
      selectionActions={selectionActions}
      library={library}
      selection={selection}
      isRootSelected={isSelected}
      onSelect={React.useCallback(
        (type) => {
          if (!editing) return;
          if (type === 'slot-item') {
            editing.sidebarNavigation.openEditPanelIfClosed();
          } else {
            // While there's no "element" edit panel, we close the sidebar
            // if you select an element
            editing.sidebarNavigation.close();
          }
        },
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editing],
      )}
      outerWidth={outerWidth ?? bp.width * BREAKPOINT_TO_CANVAS_SCALE}
      innerWidth={bp.width}
      innerViewHeight={bp.height}
      visibilityDecorators={visibilityDecorators}
      flags={session.flags}
      breakpoints={breakpoints}
    />
  );
});

const StyledSectionContent = observer(function _SectionContent({
  sref,
  element,
  selectionActions,
  isSelected,
  editing,
  session,
  selection,
  library,
  analytics,
  breakpoint,
  outerWidth,
  concept,
}: {
  sref: SectionRef | SectionRefState;
  element: ElementInstance | ElementInstanceState;
  selectionActions: SelectionActions;
  isSelected: boolean;
  editing: SectionEditingActions | undefined;
  session: ProjectSession;
  selection: SelectionStore;
  library: LibraryCache;
  analytics: AnalyticsService;
  breakpoint?: Breakpoint;
  outerWidth?: number;
  concept: ConceptAPI;
}) {
  const textEditing = React.useMemo(
    () =>
      sref.mutable && element.mutable && editing
        ? new SectionTextEditingModel(
            selection,
            sref,
            analytics,
            editing.textRewriting,
          )
        : undefined,
    [selection, element, sref, analytics, editing],
  );

  const visibilityDecorators = React.useMemo(
    () => makeSectionVisibilityDecorators(element),
    [element],
  );

  React.useEffect(
    () =>
      element.mutable && editing
        ? ensureSlotOverridesEffect(element, library, session.undo)
        : undefined,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [element, session, editing],
  );

  const bp = BREAKPOINT_SIZE[breakpoint ?? breakpointStore.get()];
  const breakpoints = React.useMemo(
    () => getBreakpointForViewport(bp.width),
    [bp],
  );

  const compiledScheme = computed(
    () => {
      if (element.id != null) {
        const shadeKey = concept.getDesignSchemeShadeKeyByElementId(element.id);
        const result = concept.schemes.find((s) => getShadeKey(s) === shadeKey);
        if (result != null) {
          return result;
        }
      }

      // Fall back to the first compiled scheme in the concept (neutral white).
      const defaultScheme = concept.schemes[0];
      if (defaultScheme == null) {
        // This should never happen.
        throw new Error('Concept has no compiled schemes');
      }
      return defaultScheme;
    },
    { equals: comparer.structural },
  ).get();

  return (
    <StyledElementView
      sref={sref}
      rootElement={element}
      textEditing={textEditing}
      selectionActions={selectionActions}
      library={library}
      selection={selection}
      isRootSelected={isSelected}
      onSelect={React.useCallback(
        (type) => {
          if (!editing) return;
          if (type === 'slot-item') {
            editing.sidebarNavigation.openEditPanelIfClosed();
          } else {
            // While there's no "element" edit panel, we close the sidebar
            // if you select an element
            editing.sidebarNavigation.close();
          }
        },
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editing],
      )}
      outerWidth={outerWidth ?? bp.width * BREAKPOINT_TO_CANVAS_SCALE}
      innerWidth={bp.width}
      innerViewHeight={bp.height}
      visibilityDecorators={visibilityDecorators}
      flags={session.flags}
      breakpoints={breakpoints}
      concept={concept}
      scheme={compiledScheme}
    />
  );
});

const EmptySectionContent = observer(function _EmptySectionContent({
  sref,
  section,
  editing,
  projectEditing,
  isSelected,
}: {
  sref: SectionRef | SectionRefState;
  section: Section | SectionState;
  editing: SectionEditingActions | undefined;
  projectEditing: ProjectEditingActions | undefined;
  isSelected: boolean;
}) {
  return (
    <div className={styles.emptySection}>
      <div className={styles.emptySectionInner}>
        <Rows spacing={16} xalign="center">
          <Rows spacing={0} xalign="center">
            <TextRegular>
              <ClickInTextarea
                enableClickIn={isSelected}
                value={section.name}
                onChange={editing?.whenComponentVisible.setName}
                placeholder="Section"
              />
            </TextRegular>
            <TextTinyLight>
              <ClickInTextarea
                enableClickIn={isSelected}
                value={section.description}
                onChange={editing?.whenComponentVisible.setDescription}
                placeholder="Add a description"
              />
            </TextTinyLight>
          </Rows>
          {projectEditing && sref.mutable && (
            <Button
              variant="secondary"
              iconColor="ai"
              icon={IconAddComponent}
              label="Add component"
              onClick={() => projectEditing.shuffleSectionComponent(sref)}
              disabled={!isUserWrittenName(section.name)}
              tooltip={
                !isUserWrittenName(section.name)
                  ? 'Please write a section name first'
                  : undefined
              }
            />
          )}
        </Rows>
      </div>
    </div>
  );
});

function ErrorSectionContent({ error }: { error: unknown }) {
  return (
    <div className={styles.emptySection}>
      <div className={styles.emptySectionInner}>
        <Rows spacing={8} xalign="center">
          <TextRegular color="red">{formatError(error)}</TextRegular>
          <TextTinyLight color="red">
            Please reload the page to try again
          </TextTinyLight>
        </Rows>
      </div>
    </div>
  );
}
